import * as React from "react";
import { toast } from "sonner";

import {
  useProjectClustersQuery,
  useToggleClusterHiddenMutation,
} from "@/entities/projects/queries";
import type { ClusterOut } from "@/entities/projects/types";
import { ApiError } from "@/shared/api/http";
import { Skeleton } from "@/shared/ui/skeleton";
import { ProjectClustersTable } from "@/widgets/ProjectClustersTable";
import { ClusterDrawer } from "@/widgets/ProjectPage/ClusterDrawer";

export interface ClustersTabProps {
  projectId: string;
}

export function ClustersTab({ projectId }: ClustersTabProps): JSX.Element {
  const [showHidden, setShowHidden] = React.useState(false);
  const clustersQ = useProjectClustersQuery(projectId, { show_hidden: showHidden });
  const [openCluster, setOpenCluster] = React.useState<ClusterOut | null>(null);
  const toggleM = useToggleClusterHiddenMutation(projectId);

  const handleToggleHidden = (cluster: ClusterOut): void => {
    toggleM.mutate(
      { clusterId: cluster.id, body: { is_hidden: !cluster.is_hidden } },
      {
        onSuccess: () =>
          toast.success(
            cluster.is_hidden ? "Кластер возвращён" : "Кластер скрыт",
          ),
        onError: (e) =>
          toast.error(
            e instanceof ApiError ? e.message : "Не удалось изменить кластер",
          ),
      },
    );
  };

  if (clustersQ.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (clustersQ.isError) {
    return (
      <div className="glass-card p-5 text-sm text-pink">
        Не удалось загрузить кластеры
      </div>
    );
  }

  const clusters = clustersQ.data?.items ?? [];
  const hiddenCount = clusters.filter((c) => c.is_hidden).length;

  if (clusters.length === 0) {
    return (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground">
        У этого проекта пока нет кластеров.
      </div>
    );
  }

  return (
    <>
      <ProjectClustersTable
        clusters={clusters}
        onRowClick={setOpenCluster}
        onToggleHidden={handleToggleHidden}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
        hiddenCount={hiddenCount}
      />
      <ClusterDrawer
        projectId={projectId}
        cluster={openCluster}
        onClose={() => setOpenCluster(null)}
      />
    </>
  );
}
